import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const LAB_TOPICS = [
  'Anti-Corruption & Bribery',
  'Anti-Money Laundering',
  'Data Privacy & Protection',
  'Conflict of Interest',
  'Workplace Harassment',
  'Information Security',
  'Fraud Prevention',
  'Regulatory Compliance',
];

export const LAB_INDUSTRIES = [
  'Financial Services',
  'Healthcare',
  'Technology',
  'Manufacturing',
  'Retail',
  'Government',
  'Education',
  'General Business',
];

const LEVEL_HINTS = {
  Introductory: '2 decisions · plain language · one clearly best answer · encouraging feedback',
  Foundational: '3 decisions · straightforward situations · supportive feedback',
  Intermediate: '4 decisions · realistic pressures · all options plausible',
  Advanced: '5 decisions · subtle trade-offs and ambiguity',
};

export const EMPTY_LAB_FORM = { title: '', topic: '', industry: '', level: 'Foundational', description: '', outline: '' };

export const isLabFormValid = (form) => !!(form.title && form.topic && form.industry);

// Payload for POST /api/ai/generate-lab — the single Learning Lab generator.
export const toLabGeneratePayload = (form) => ({
  title: form.title,
  topic: form.topic,
  industry: form.industry,
  level: form.level,
  description: form.description,
  outline: form.outline,
});

// Shared "Create Learning Lab" fields used by Admin > Learning Labs and the Programme Builder.
export function LabGenerateFields({ form, setForm, showTitle = true, inputClass = 'mt-1.5 bg-zinc-900 border-zinc-700' }) {
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  return (
    <div className="space-y-4" data-testid="lab-generate-fields">
      {showTitle && (
        <div>
          <Label>Lab Title *</Label>
          <Input value={form.title} onChange={(e) => set('title')(e.target.value)} placeholder="e.g., Handling a Bribery Attempt" className={inputClass} data-testid="lab-gen-title" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Topic *</Label>
          <Select value={form.topic} onValueChange={set('topic')}>
            <SelectTrigger className={inputClass} data-testid="lab-gen-topic"><SelectValue placeholder="Select topic" /></SelectTrigger>
            <SelectContent>
              {LAB_TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Industry *</Label>
          <Select value={form.industry} onValueChange={set('industry')}>
            <SelectTrigger className={inputClass} data-testid="lab-gen-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
            <SelectContent>
              {LAB_INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Level</Label>
        <Select value={form.level} onValueChange={set('level')}>
          <SelectTrigger className={inputClass} data-testid="lab-gen-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Introductory">Introductory (Easy)</SelectItem>
            <SelectItem value="Foundational">Foundational</SelectItem>
            <SelectItem value="Intermediate">Intermediate</SelectItem>
            <SelectItem value="Advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-zinc-500 mt-1" data-testid="lab-gen-level-hint">{LEVEL_HINTS[form.level] || LEVEL_HINTS.Foundational}</p>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => set('description')(e.target.value)} placeholder="Brief description of the scenario..." className={inputClass} rows={2} data-testid="lab-gen-description" />
      </div>
      <div>
        <Label>Scenario Outline (Optional)</Label>
        <Textarea value={form.outline} onChange={(e) => set('outline')(e.target.value)} placeholder="Provide an outline or key points you want included in the scenario..." className={inputClass} rows={3} data-testid="lab-gen-outline" />
        <p className="text-xs text-zinc-500 mt-1">Leave empty for AI to create the full scenario</p>
      </div>
    </div>
  );
}

// Runs the single Learning Lab generator as a background job and resolves with the new lab_id.
export async function generateLearningLab(axiosInstance, api, token, form, onTick) {
  const headers = { Authorization: `Bearer ${token}` };
  const { data } = await axiosInstance.post(`${api}/ai/generate-lab/jobs`, toLabGeneratePayload(form), { headers });
  const started = Date.now();
  while (Date.now() - started < 300000) {
    await new Promise((r) => setTimeout(r, 4000));
    onTick?.(Math.min(95, Math.round(((Date.now() - started) / 120000) * 100)));
    const res = await axiosInstance.get(`${api}/ai/generate-lab/jobs/${data.job_id}`, { headers });
    if (res.data.status === 'completed') return res.data.lab_id;
    if (res.data.status === 'failed') throw new Error(res.data.error || 'Generation failed');
  }
  throw new Error('Generation is taking longer than expected — check Admin > Learning Labs shortly');
}
